// Shared by the slip editor, submit_pilot_slip and scripts/base_time_audit.mjs
// (moved from src/constants on 2026-10-11). Plain JavaScript, no imports.
// Prompted slip events (John's detention reboot, 2026-07-31).
//
// Pilots record WHAT HAPPENED from the Event menu; they never decide whether
// something is detention. The office computes billing in FileMaker from the
// event's times and place (in-port vs outside, hour caps, overlaps). Detention
// outside pilotage waters is computed office-side from the transits and needs
// no event at all.
//
// `description` strings are John's exact event descriptions (2026-07-31 memo)
// and are part of the FileMaker JSON contract — never reword them.
export const SLIP_EVENTS = {
  ANC: {
    kinds: ["cruise", "cargo"], // the cargo sheet's "Anchoring/Laying-to"
    menu: "Anchored (outside of a port)",
    badge: "ANCHORED",
    description: "ANC",
    times: "both", // start = anchor down, end = anchor aweigh
  },
  DETENTION: {
    // Laying-to and DP mode merged per John 2026-07-31: no billing
    // difference, one event.
    kinds: ["cruise", "cargo"],
    menu: "Laying to / DP mode (underway but not making way)",
    badge: "DETENTION",
    description: "Detention",
    times: "both",
  },
  PORT_DETENTION: {
    // Cruise only: the cargo rate sheet has one Detention line, not a
    // separate in-port kind.
    kinds: ["cruise"],
    menu: "Stayed aboard in port (master's request)",
    badge: "PORT DETENTION",
    // Merged word, capital D — John's exact code (Jill confirmed
    // 2026-07-31). FileMaker matches this string byte for byte, so a
    // lowercase d or an added space silently drops port detentions from
    // his import. Do not "tidy" the casing.
    description: "PortDetention",
    times: "both",
  },
  SHIFT: {
    kinds: ["cruise", "cargo"],
    menu: "Shifted within port (anchor→dock, dock→dock, …)",
    badge: "SHIFT",
    description: "Shift",
    times: "single", // the one time the ship started the move (OUT side)
  },
  SAILING_DELAY: {
    // Timestamps only (John 2026-07-31): start = planned sailing time,
    // end = actual sailing time. FileMaker does the math and applies the
    // sailing-delay hour cap, so the portal sends NO hours for timed rows.
    kinds: ["cruise", "cargo"],
    menu: "Sailing delay (sailed 1+ hour past planned time)",
    badge: "SAILING DELAY",
    description: "SailingDelay",
    times: "both",
  },
  MEDIVAC: {
    kinds: ["cruise"],
    menu: "Medical evacuation (diverted to another area)",
    badge: "MEDIVAC",
    description: "MEDIVAC",
    times: "both",
  },

  // --- cargo only (Jill 2026-08-23) ---------------------------------------
  // From the 2026 Bulk Dry Cargo published rates. These three are the only
  // cargo charges that are neither already here nor already a grid row:
  //
  //   Port arrival / departure   the cargo instructions say to use "first
  //                              line/anchored for arrival, and last line/
  //                              anchor aweigh for departure" — those are the
  //                              port row's own IN and OUT times.
  //
  // Recording those here as well would give a pilot two places to enter one
  // thing, which is how a shift ends up billed twice.
  //
  // The rate sheet's six "Waterway Areas" are not here either, and are not in
  // the cargo location list: Jill ruled them out entirely (2026-08-23) — five
  // are defunct cargo areas and no cargo runs Wrangell Narrows.
  //
  // PROVISIONAL DESCRIPTIONS. The strings above are John's exact FileMaker
  // wording for cruise; these three have not been through him yet (his Q9 and
  // Q3 are open). Nothing is sent to the office for cargo or yacht while
  // CARGO_YACHT_OFFICE_DELIVERY is off, so a wrong guess costs a code edit and
  // nothing else — but they must be confirmed before that switch is flipped.
  DEAD_SHIP: {
    kinds: ["cargo"],
    menu: "Dead ship movement",
    badge: "DEAD SHIP",
    description: "DeadShip",
    times: "single", // the time the movement started, like a shift
  },
  WATCH_OVER_8: {
    kinds: ["cargo"],
    menu: "Continuous watch over 8 hours",
    badge: "WATCH >8",
    description: "WatchOver8",
    times: "both", // the office needs the span to work out the excess
  },
  STANDBY: {
    kinds: ["cargo"],
    menu: "Standby",
    badge: "STANDBY",
    description: "Standby",
    times: "both",
  },
};

// Menu order matches John's prompted-events list, cargo's additions after it.
export const SLIP_EVENT_MENU = [
  "ANC",
  "DETENTION",
  "PORT_DETENTION",
  "SHIFT",
  "SAILING_DELAY",
  "MEDIVAC",
  "DEAD_SHIP",
  "WATCH_OVER_8",
  "STANDBY",
];

// The menu a given slip shows. An unknown kind gets the cruise menu, which is
// what every trip predating trip_kind is.
export function slipEventMenuFor(tripKind) {
  const kind = ["cruise", "cargo", "yacht"].includes(tripKind) ? tripKind : "cruise";
  return SLIP_EVENT_MENU.filter((code) => (SLIP_EVENTS[code].kinds || ["cruise"]).includes(kind));
}

// Classify a segment row as one of the prompted events, or null for a real
// place row. New rows carry event_code directly; older drafts are recognized
// by their detention_type and the row-name conventions that predate
// event_code, so a season's worth of in-flight drafts keeps working.
export function slipEventCode(seg) {
  if (!seg) return null;
  if (seg.event_code && SLIP_EVENTS[seg.event_code]) return seg.event_code;
  const name = (seg.location_name || "").toUpperCase();
  if (seg.detention_type === "SAILING_DELAY" || /^\s*SAILING DELAY\b/.test(name)) return "SAILING_DELAY";
  if (seg.detention_type === "PORT") return "PORT_DETENTION";
  if (seg.detention_type === "LAYING_TO" || seg.detention_type === "DP") return "DETENTION";
  if (seg.rate_event_code === "SHIFT" || /^\s*SHIFT\b/.test(name)) return "SHIFT";
  if (/^\s*PORT DETENTION\b/.test(name)) return "PORT_DETENTION";
  // Legacy "Other" detention rows (detention_type null) classify as DETENTION
  // for row semantics; whether they travel in filemaker_detention is decided
  // by the JSON builder (event_code / detention_type present), matching the
  // old behavior where type-less rows printed on the PDF only.
  if (/\bDETENTION\b/.test(name)) return "DETENTION";
  if (/^\s*ANCHORED\b/.test(name)) return "ANC";
  if (/^\s*MEDIVAC\b/.test(name)) return "MEDIVAC";
  return null;
}
